//
// scheduleAlgorithm.js
//
// Computes valid schedule combinations using backtracking and scores each valid combination.
// Supports ignoring recitations, user-defined breaks, etc.
// Each returned schedule includes a normalized score from 0 to 100 (100 being optimal).
//

/**
 * Parses a time string (e.g. '10:30 AM', '19:00') into minutes from midnight.
 */
function parseTimeGeneric(timeStr) {
  if (!timeStr) return 0;

  // Handle AM/PM
  const lower = timeStr.toLowerCase().replace(/\s/g, "").replace(/\./g, "");
  const isPM = lower.includes("pm");
  const isAM = lower.includes("am");
  const cleaned = lower.replace("am", "").replace("pm", "");
  const parts = cleaned.split(":");
  let hours = parseInt(parts[0], 10) || 0;
  const minutes = parts[1] ? parseInt(parts[1], 10) : 0;

  if (isPM && hours < 12) {
    hours += 12;
  }
  if (isAM && hours === 12) {
    hours = 0;
  }
  return hours * 60 + minutes;
}

/**
 * Helper function to get a user-friendly course display name.
 */
function getCourseDisplayName(course) {
  const dept = course.dept ? course.dept.toUpperCase() : "";
  const number = course.number || "";
  if (course.section && course.section.trim() !== "" && course.section.toUpperCase() !== "DEFAULT") {
    return `${dept} ${number} ${course.section.toUpperCase()}`;
  }
  return `${dept} ${number}`;
}

/**
 * Gathers instructors from the SFU data object (which can appear in multiple places).
 */
function gatherInstructorsFromSfuData(sfuData) {
  const out = [];
  if (!sfuData) return out;

  // sfuData.instructor can be a single object or an array
  if (Array.isArray(sfuData.instructor)) {
    out.push(...sfuData.instructor);
  } else if (sfuData.instructor) {
    out.push(sfuData.instructor);
  }

  // Sometimes sfuData.info.instructors is an array
  if (sfuData.info && Array.isArray(sfuData.info.instructors)) {
    out.push(...sfuData.info.instructors);
  }

  return out;
}

/**
 * Deduplicates instructors based on their name/commonName property.
 */
function uniqueInstructors(instructorArray) {
  const seen = new Set();
  const result = [];
  for (const inst of instructorArray) {
    const nameKey = inst.name || inst.commonName || JSON.stringify(inst);
    if (!seen.has(nameKey)) {
      seen.add(nameKey);
      result.push(inst);
    }
  }
  return result;
}

/**
 * Given a course with multiple sections (lectures + recitations),
 * tries to pair up each lecture with its corresponding recitation(s),
 * merging them into a single "composite" schedule.
 */
function pairLectureAndRecitations(course) {
  // Identify all "lecture" sections
  const lectures = course.availableSections.filter((opt) => {
    return (
      opt.sfuData &&
      opt.sfuData.courseSchedule &&
      (
        // classType === "e" or sectionCode === "LEC"
        opt.sfuData.courseSchedule.some(
          (sched) => sched.classType === "e" || sched.sectionCode === "LEC"
        ) ||
        // or the section name ends in "00" (common for lecture #)
        (typeof opt.section === "string" && opt.section.toLowerCase().endsWith("00"))
      )
    );
  });

  // If we can't identify any "lecture," fallback to returning any valid sections
  if (lectures.length === 0) {
    return course.availableSections
      .filter((opt) =>
        opt.sfuData &&
        opt.sfuData.courseSchedule &&
        opt.sfuData.courseSchedule.some(
          (sched) => !sched.isExam && sched.days && sched.startTime && sched.endTime
        )
      )
      .map((opt) => ({
        dept: course.dept,
        number: course.number,
        section: opt.section,
        sfuData: opt.sfuData,
      }));
  }

  // Make sure we only keep each lecture section once
  const uniqueLectures = [];
  const seen = new Set();
  lectures.forEach((lecture) => {
    const sec = lecture.section.toLowerCase();
    if (!seen.has(sec)) {
      seen.add(sec);
      uniqueLectures.push(lecture);
    }
  });

  // Identify possible recitations (classType === "n" or non-lecture code)
  const recitations = course.availableSections.filter((opt) => {
    return (
      opt.sfuData &&
      opt.sfuData.courseSchedule &&
      (
        opt.sfuData.courseSchedule.some(
          (sched) => sched.classType === "n" && sched.sectionCode !== "LEC"
        ) ||
        !(typeof opt.section === "string" && opt.section.toLowerCase().endsWith("00"))
      )
    );
  });

  const compositeOptions = [];

  uniqueLectures.forEach((lecture) => {
    const lectureMeeting = lecture.sfuData.courseSchedule.find(
      (sched) => sched.classType === "e" || sched.sectionCode === "LEC"
    );
    const assoc = lectureMeeting ? lectureMeeting.associatedClass : null;

    let matchedRecitations = [];

    // 1) Try matching recitations on associatedClass
    if (assoc) {
      matchedRecitations = recitations.filter((rec) =>
        rec.sfuData.courseSchedule.some((sched) => sched.associatedClass === assoc)
      );
    }

    // 2) If no match, try a fallback numeric approach (e.g., LEC 001 might pair with 01D / 01R)
    if (matchedRecitations.length === 0) {
      const lecNum = parseInt(lecture.section.replace(/^\D+/, ""), 10);
      if (!isNaN(lecNum)) {
        matchedRecitations = recitations.filter((rec) => {
          const recNum = parseInt(rec.section.replace(/^\D+/, ""), 10);
          return (
            !isNaN(recNum) &&
            Math.floor(recNum / 100) === Math.floor(lecNum / 100) &&
            recNum !== lecNum
          );
        });
      }
    }

    // 3) If we have matched recitations, produce "composite" combos
    if (matchedRecitations.length > 0) {
      matchedRecitations.forEach((recitation) => {
        const merged = [
          ...lecture.sfuData.courseSchedule,
          ...recitation.sfuData.courseSchedule,
        ];
        const seenMeetings = new Set();
        const mergedUnique = [];
        merged.forEach((meeting) => {
          const key = `${meeting.startTime}-${meeting.endTime}-${meeting.days}-${meeting.classType}-${meeting.location}`;
          if (!seenMeetings.has(key)) {
            seenMeetings.add(key);
            mergedUnique.push(meeting);
          }
        });
        const lectureInstr = gatherInstructorsFromSfuData(lecture.sfuData);
        const recInstr = gatherInstructorsFromSfuData(recitation.sfuData);
        const combinedInstr = uniqueInstructors([...lectureInstr, ...recInstr]);
        compositeOptions.push({
          dept: course.dept,
          number: course.number,
          section: `${lecture.section}+${recitation.section}`,
          sfuData: {
            courseSchedule: mergedUnique,
            instructor: combinedInstr,
          },
          composite: { lecture, recitation },
        });
      });
    } else {
      // 4) No matched recitations => treat the lecture alone as a valid option
      const singleInstr = gatherInstructorsFromSfuData(lecture.sfuData);
      compositeOptions.push({
        dept: course.dept,
        number: course.number,
        section: lecture.section,
        sfuData: {
          ...lecture.sfuData,
          instructor: singleInstr,
        },
        composite: { lecture },
      });
    }
  });

  return compositeOptions;
}

/**
 * Creates a key for a course like MATH_150_DEFAULT or MATH_150_D100, etc.
 */
function getCourseKey(course) {
  const dept = course.dept.toUpperCase();
  const number = course.number;
  const section = course.section ? course.section.toUpperCase() : "DEFAULT";
  return `${dept}_${number}_${section}`;
}

/**
 * Checks if two sets of course-schedule meetings overlap on the same day.
 * Also considers afterClassBreak if enabled in preferences.
 */
function isConflict(schedule1, schedule2, prefs) {
  for (const m1 of schedule1) {
    if (m1.isExam) continue;
    const days1 = m1.days ? m1.days.split(",").map((d) => d.trim()) : [];
    const start1 = parseTimeGeneric(m1.startTime || "0:00");
    const end1 = parseTimeGeneric(m1.endTime || "0:00");

    for (const m2 of schedule2) {
      if (m2.isExam) continue;
      const days2 = m2.days ? m2.days.split(",").map((d) => d.trim()) : [];
      const start2 = parseTimeGeneric(m2.startTime || "0:00");
      const end2 = parseTimeGeneric(m2.endTime || "0:00");

      // If they share any day, check for overlap
      if (days1.some((day) => days2.includes(day))) {
        // Basic overlap check
        if (start1 < end2 && end1 > start2) {
          return true;
        }
        // If afterClassBreak is enabled, ensure there's enough gap
        if (prefs.afterClassBreakEnabled) {
          const needed = parseInt(prefs.afterClassBreakDuration, 10) || 0;
          const gap1 = start2 - end1;
          const gap2 = start1 - end2;
          if ((gap1 > 0 && gap1 < needed) || (gap2 > 0 && gap2 < needed)) {
            return true;
          }
        }
      }
    }
  }
  return false;
}

/**
 * Checks if a given set of meetings conflicts with user-defined breaks.
 */
function isConflictWithBreaks(schedule, breaks) {
  for (const m of schedule) {
    if (m.isExam) continue;
    const days = m.days ? m.days.split(",").map((d) => d.trim()) : [];
    const start = parseTimeGeneric(m.startTime || "0:00");
    const end = parseTimeGeneric(m.endTime || "0:00");

    // Compare each selected break
    for (const bk of breaks) {
      if (!bk.selected) continue; // only consider "active" breaks
      // If they share any day in common...
      if (bk.days.some((day) => days.includes(day))) {
        const bkStart = parseTimeGeneric(bk.start || "0:00");
        const bkEnd = parseTimeGeneric(bk.end || "0:00");

        // If there's overlap between [start, end] and [bkStart, bkEnd], conflict
        if (start < bkEnd && end > bkStart) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Main function that computes all valid schedule combinations.
 * Returns an array of objects: { combination: [...], score: number }.
 */
function computeScheduleCombinations(courseSchedules, breaks, options, selectedSections) {
  const { ignoreRecitations, preferences } = options;

  // Convert each course into its possible "section combos" (or single direct data).
  // If there's a locked section in selectedSections, we respect that only.
  const optionsArray = courseSchedules.map((course) => {
    const key = getCourseKey(course);

    // 1. If user locked a specific section in 'selectedSections',
    //    only that one is allowed
    if (selectedSections && selectedSections[key]) {
      return [selectedSections[key]];
    }

    // 2. If multiple sections exist
    if (course.availableSections && course.availableSections.length > 0) {
      if (ignoreRecitations) {
        // Just pick each "lecture" as a separate option
        let lectures = course.availableSections.filter((opt) => {
          return (
            opt.sfuData &&
            opt.sfuData.courseSchedule &&
            opt.sfuData.courseSchedule.some(
              (sched) => sched.classType === "e" || sched.sectionCode === "LEC"
            )
          );
        });

        // If no standard "lecture" found, fallback to any valid
        if (lectures.length === 0) {
          return course.availableSections
            .filter(
              (opt) =>
                opt.sfuData &&
                opt.sfuData.courseSchedule &&
                opt.sfuData.courseSchedule.some(
                  (sched) => !sched.isExam && sched.days && sched.startTime && sched.endTime
                )
            )
            .map((opt) => ({
              dept: course.dept,
              number: course.number,
              section: opt.section,
              sfuData: opt.sfuData,
            }));
        }
        // Deduplicate possible lectures
        const seenSec = new Set();
        const uniqueLectures = [];
        lectures.forEach((lec) => {
          const sec = lec.section.toLowerCase();
          if (!seenSec.has(sec)) {
            seenSec.add(sec);
            uniqueLectures.push({
              dept: course.dept,
              number: course.number,
              section: lec.section,
              sfuData: lec.sfuData,
            });
          }
        });
        return uniqueLectures;
      } else {
        // Normal recitation-aware approach
        const composites = pairLectureAndRecitations(course);
        return composites.length > 0 ? composites : [course];
      }
    }
    // 3. If single direct data: just that course
    else if (
      course.sfuData &&
      course.sfuData.courseSchedule &&
      course.sfuData.courseSchedule.length > 0
    ) {
      return [course];
    }
    // 4. No data => empty array
    return [];
  });

  // If any course yields zero options, no valid combos can exist
  if (optionsArray.some((arr) => arr.length === 0)) {
    return [];
  }

  // Collect all possible combinations using backtracking
  const results = [];

  function backtrack(index, currentCombination) {
    if (index === optionsArray.length) {
      // If we've chosen one valid "section" for each course, record it
      results.push([...currentCombination]);
      return;
    }

    for (const option of optionsArray[index]) {
      let conflict = false;

      // Check conflicts with already-chosen sections
      for (const selected of currentCombination) {
        if (isConflict(selected.sfuData.courseSchedule, option.sfuData.courseSchedule, preferences)) {
          conflict = true;
          break;
        }
      }

      // Also check if this new option conflicts with user breaks
      if (!conflict && isConflictWithBreaks(option.sfuData.courseSchedule, breaks)) {
        conflict = true;
      }

      if (!conflict) {
        currentCombination.push(option);
        backtrack(index + 1, currentCombination);
        currentCombination.pop();
      }
    }
  }

  backtrack(0, []);

  // Now apply preference filters (like enforceStartEnd / enforceLunch) to remove combos
  let validResults = results;

  // enforceStartEnd: drop combos that have any course outside desired start/end
  if (preferences.enforceStartEnd) {
    validResults = validResults.filter((comb) => {
      const desiredStart = parseTimeGeneric(preferences.desiredStart || "08:00");
      const desiredEnd = parseTimeGeneric(preferences.desiredEnd || "17:00");

      // Flatten all non-exam meetings
      const allMeetings = [];
      comb.forEach((option) => {
        if (option.sfuData && option.sfuData.courseSchedule) {
          option.sfuData.courseSchedule.forEach((m) => {
            if (!m.isExam) {
              allMeetings.push(m);
            }
          });
        }
      });

      // If *any* meeting is outside [desiredStart, desiredEnd], combo is invalid
      return allMeetings.every((m) => {
        const mStart = parseTimeGeneric(m.startTime || "0:00");
        const mEnd = parseTimeGeneric(m.endTime || "0:00");
        return mStart >= desiredStart && mEnd <= desiredEnd;
      });
    });
  }

  // enforceLunch: drop combos that have any meeting crossing lunch window
  if (preferences.enforceLunch) {
    validResults = validResults.filter((comb) => {
      const lunchStart = parseTimeGeneric(preferences.lunchStart || "12:00");
      const lunchEnd = parseTimeGeneric(preferences.lunchEnd || "13:00");

      const allMeetings = [];
      comb.forEach((option) => {
        if (option.sfuData && option.sfuData.courseSchedule) {
          option.sfuData.courseSchedule.forEach((m) => {
            if (!m.isExam) {
              allMeetings.push(m);
            }
          });
        }
      });

      // If any meeting overlaps [lunchStart, lunchEnd], combo is invalid
      return allMeetings.every((m) => {
        const mStart = parseTimeGeneric(m.startTime || "0:00");
        const mEnd = parseTimeGeneric(m.endTime || "0:00");
        // Overlap if (mStart < lunchEnd) && (mEnd > lunchStart)
        return !(mStart < lunchEnd && mEnd > lunchStart);
      });
    });
  }

  // Score each valid combination
  const intermediate = validResults.map((comb) => {
    const rawScore = scoreCombination(comb, preferences);
    return { combination: comb, rawScore };
  });

  // Deduplicate combos by signature (in case of duplicates)
  const uniqueCombMap = new Map();
  intermediate.forEach((item) => {
    const sig = item.combination
      .map((o) => `${o.dept}-${o.number}-${o.section.toLowerCase()}`)
      .join("|");
    if (!uniqueCombMap.has(sig)) {
      uniqueCombMap.set(sig, item);
    }
  });

  const unique = Array.from(uniqueCombMap.values());
  if (unique.length === 0) return [];

  // Normalize scores: higher rawScore => "worse", we invert to get 0..100 scale
  const rawScores = unique.map((u) => u.rawScore);
  const minScore = Math.min(...rawScores);
  const maxScore = Math.max(...rawScores);

  unique.forEach((u) => {
    // If they're all the same, everything is 100
    if (maxScore === minScore) {
      u.normalizedScore = 100;
    } else {
      // Invert so that lower rawScore => higher normalized
      u.normalizedScore = Math.round(
        100 * (maxScore - u.rawScore) / (maxScore - minScore)
      );
    }
  });

  // Sort final combos ascending by rawScore (so "best" is last if you want it reversed)
  unique.sort((a, b) => a.rawScore - b.rawScore);

  // Return combos with normalized score
  return unique.map((u) => ({
    combination: u.combination,
    score: u.normalizedScore,
  }));
}

/**
 * Applies a "soft" scoring system, returning a larger raw score for "worse" schedules.
 * We'll invert it later to produce normalizedScore in [0..100].
 */
function scoreCombination(combination, preferences) {
  let score = 0;

  // Flatten all non-exam meetings
  const allMeetings = [];
  combination.forEach((option) => {
    if (option.sfuData && option.sfuData.courseSchedule) {
      option.sfuData.courseSchedule.forEach((m) => {
        if (!m.isExam) allMeetings.push(m);
      });
    }
  });
  if (allMeetings.length === 0) return score;

  // Earliest start, latest end
  const globalEarliest = Math.min(
    ...allMeetings.map((m) => parseTimeGeneric(m.startTime || "0:00"))
  );
  const globalLatest = Math.max(
    ...allMeetings.map((m) => parseTimeGeneric(m.endTime || "0:00"))
  );

  const desiredStart = parseTimeGeneric(preferences.desiredStart || "08:00");
  const desiredEnd = parseTimeGeneric(preferences.desiredEnd || "17:00");

  // The further away from (desiredStart, desiredEnd), the more we penalize
  if (globalEarliest < desiredStart) {
    score += preferences.weightStart * (desiredStart - globalEarliest);
  }
  if (globalLatest > desiredEnd) {
    score += preferences.weightEnd * (globalLatest - desiredEnd);
  }

  // If a course overlaps lunch, big penalty
  const lunchStart = parseTimeGeneric(preferences.lunchStart || "12:00");
  const lunchEnd = parseTimeGeneric(preferences.lunchEnd || "13:00");
  allMeetings.forEach((m) => {
    const mStart = parseTimeGeneric(m.startTime || "0:00");
    const mEnd = parseTimeGeneric(m.endTime || "0:00");
    if (mStart < lunchEnd && mEnd > lunchStart) {
      score += preferences.weightLunch * 1000;
    }
  });

  // After-class gap: if there's less than X minutes between back-to-back classes, penalize
  const sortedMeetings = allMeetings
    .slice()
    .sort((a, b) => parseTimeGeneric(a.startTime) - parseTimeGeneric(b.startTime));

  if (preferences.afterClassBreakEnabled) {
    const needed = parseInt(preferences.afterClassBreakDuration, 10) || 0;
    for (let i = 0; i < sortedMeetings.length - 1; i++) {
      const endCurrent = parseTimeGeneric(sortedMeetings[i].endTime);
      const startNext = parseTimeGeneric(sortedMeetings[i + 1].startTime);
      const gap = startNext - endCurrent;
      if (gap < needed && gap > 0) {
        score += preferences.weightAfterClassBreak * (needed - gap);
      }
    }
  }

  return score;
}

/* --- Helper functions for error-reporting or debugging (optional) --- */

/**
 * Generates direct conflict messages for two courses that overlap.
 */
function groupCourseConflicts(conflictObjects) {
  const groups = new Map();
  conflictObjects.forEach((conflict) => {
    if (conflict.type === "courseConflict") {
      const courseAName = getCourseDisplayName(conflict.courseA);
      const courseBName = getCourseDisplayName(conflict.courseB);
      const groupKey = [courseAName, courseBName].sort().join(" & ");
      if (!groups.has(groupKey)) {
        groups.set(groupKey, new Set([courseAName, courseBName]));
      } else {
        groups.get(groupKey).add(courseAName);
        groups.get(groupKey).add(courseBName);
      }
    }
  });
  return Array.from(groups.values()).map((groupSet) => Array.from(groupSet));
}

function getCoursesMissingData(courseSchedules) {
  return courseSchedules.filter((course) => {
    const hasDirectData =
      course.sfuData &&
      course.sfuData.courseSchedule &&
      course.sfuData.courseSchedule.length > 0;

    const hasSectionData =
      course.availableSections &&
      course.availableSections.some(
        (sec) => sec.sfuData && sec.sfuData.courseSchedule && sec.sfuData.courseSchedule.length > 0
      );

    return !(hasDirectData || hasSectionData);
  });
}

function generateDetailedSuggestions(conflictObjects, courseSchedules) {
  const suggestions = [];

  // 2. Grouped conflict messages
  const groupedConflicts = groupCourseConflicts(conflictObjects);
  if (groupedConflicts.length > 0) {
    groupedConflicts.forEach((group) => {
      if (group.length > 1) {
        suggestions.push(
          `The following courses conflict: ${group.join(" & ")}. Consider choosing alternative sections or removing one.`
        );
      }
    });
  }

  // 3. Break conflicts
  const breakConflicts = conflictObjects.filter((c) => c.type === "breakConflict");
  if (breakConflicts.length > 0) {
    suggestions.push(
      "Some classes conflict with your break times. Consider adjusting your break schedule."
    );
  }

  // 4. Preference conflicts
  const preferenceConflicts = conflictObjects.filter((c) => c.type === "preferenceConflict");
  if (preferenceConflicts.length > 0) {
    suggestions.push(
      "Some courses are outside your preferred times. Consider adjusting your preferences."
    );
  }

  // 5. Missing data
  const missingDataCourses = getCoursesMissingData(courseSchedules);
  if (missingDataCourses.length > 0) {
    const missingList = missingDataCourses.map((course) => getCourseDisplayName(course)).join(", ");
    suggestions.push(`The following courses are missing schedule data: ${missingList}.`);
  }

  // Deduplicate suggestions
  return Array.from(new Set(suggestions));
}

export {
  computeScheduleCombinations,
  parseTimeGeneric,
  generateDetailedSuggestions,
  getCourseKey,
};
